import React, { useState, useRef } from 'react';
import { Star, X, Upload, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useTheme } from '@/contexts/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FEATURE_LABELS = {
  cat_tool: 'CAT Tool',
  mtpe_pro: 'MTPE Pro',
  proofread: 'Proofreading',
  tqa: 'TQA',
  smart_review: 'Smart Review'
};

const ExportFeedbackModal = ({ feature, onClose }) => {
  const [rating, setRating] = useState(0);
  const [hovered, setHovered] = useState(0);
  const [comment, setComment] = useState('');
  const [screenshot, setScreenshot] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);
  const { isDarkMode } = useTheme();

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Only image files allowed');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Screenshot must be under 5MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => setScreenshot(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error('Please select a star rating');
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/feedback/export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          feature,
          rating,
          comment: comment.trim(),
          screenshot_base64: screenshot || ''
        })
      });
      if (res.ok) {
        toast.success('Thanks for your feedback!');
        onClose();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to submit');
      }
    } catch {
      toast.error('Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`fixed bottom-6 right-6 z-50 w-80 rounded-2xl shadow-2xl border p-5 animate-in slide-in-from-bottom-4 ${
      isDarkMode ? 'bg-[#111827] border-gray-700' : 'bg-white border-gray-100'
    }`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className={`text-sm font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>How was your experience?</p>
          <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{FEATURE_LABELS[feature] || feature}</p>
        </div>
        <button
          onClick={onClose}
          className={`p-1 rounded-lg transition-colors ${
            isDarkMode
              ? 'text-gray-500 hover:text-gray-300 hover:bg-gray-700'
              : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
          }`}
        >
          <X size={16} />
        </button>
      </div>

      <div className="flex gap-1 mb-4">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            onMouseEnter={() => setHovered(star)}
            onMouseLeave={() => setHovered(0)}
            onClick={() => setRating(star)}
            className="transition-transform hover:scale-110"
          >
            <Star
              size={28}
              className={`transition-colors ${
                star <= (hovered || rating)
                  ? 'fill-amber-400 text-amber-400'
                  : (isDarkMode ? 'text-gray-600' : 'text-gray-300')
              }`}
            />
          </button>
        ))}
      </div>

      <textarea
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder="Optional comment..."
        rows={2}
        className={`w-full text-sm border rounded-xl px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 mb-3 ${
          isDarkMode
            ? 'bg-[#1e293b] border-gray-600 text-gray-200 placeholder-gray-500'
            : 'bg-white border-gray-200 text-gray-900'
        }`}
      />

      <div className="mb-4">
        {screenshot ? (
          <div className="relative">
            <img src={screenshot} alt="screenshot" className="w-full h-20 object-cover rounded-xl" />
            <button
              onClick={() => setScreenshot(null)}
              className={`absolute top-1 right-1 p-1 rounded-full shadow ${
                isDarkMode ? 'bg-gray-700 text-gray-300 hover:text-red-400' : 'bg-white text-gray-500 hover:text-red-500'
              }`}
            >
              <X size={12} />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className={`flex items-center gap-2 text-xs transition-colors ${
              isDarkMode ? 'text-gray-400 hover:text-blue-400' : 'text-gray-500 hover:text-blue-600'
            }`}
          >
            <Upload size={13} />
            Attach screenshot (optional, max 5MB)
          </button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="hidden"
        />
      </div>

      <div className="flex gap-2">
        <button
          onClick={onClose}
          className={`flex-1 px-3 py-2 text-sm border rounded-xl transition-colors ${
            isDarkMode
              ? 'text-gray-300 border-gray-600 hover:bg-gray-700'
              : 'text-gray-600 border-gray-200 hover:bg-gray-50'
          }`}
        >
          Dismiss
        </button>
        <button
          onClick={handleSubmit}
          disabled={submitting || rating === 0}
          className="flex-1 px-3 py-2 text-sm font-semibold bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-1"
        >
          {submitting ? <Loader2 size={14} className="animate-spin" /> : null}
          Submit
        </button>
      </div>
    </div>
  );
};

export default ExportFeedbackModal;
